// ROI Reporting — the metric-linked ROI model.
//
// Each lever (sleep duration, sleep quality, resting heart rate, HRV, vigorous
// activity) is a cohort "then vs now" average converted to a £ value using one
// published study plus a clearly separated bridging assumption — the step we
// added ourselves to turn that study into a formula. Company numbers
// (headcount, programme cost, participation rate, attribution rate, absence
// costs) are admin-entered per client. Participation rate is a measured
// figure (pull it from the Management Hub); attribution rate is a genuine
// judgement call. Nothing here calls the backend — it is a pure, deterministic
// calculator so every total is auditable line-by-line back to its formula.

export interface RoiCompanyInputs {
  /** Employees in the programme. */
  employees: number;
  /** Fully loaded average staff cost, £/yr. */
  averageSalary: number;
  /** Programme cost per employee, £/yr — what running Peak4 costs the client. */
  programmeCostPerEmployee: number;
  /** Fully loaded cost of one lost working day, £. */
  costPerSickDay: number;
  /** Baseline sickness-absence days per employee per year. */
  absenceDaysPerEmployee: number;
  /**
   * Share of employees actively engaging with the programme, 0..1. A measured
   * figure — pull it from the Management Hub rather than guessing.
   */
  participationRate: number;
  /**
   * Share of any improvement credited to the programme rather than other
   * causes, 0..1. A genuine judgement call, not a published figure.
   */
  attributionRate: number;
}

export const DEFAULT_COMPANY_INPUTS: RoiCompanyInputs = {
  employees: 250,
  averageSalary: 45000,
  programmeCostPerEmployee: 400,
  costPerSickDay: 120,
  absenceDaysPerEmployee: 9.4,
  participationRate: 0.6,
  attributionRate: 0.4,
};

/**
 * Sales/demo preset (PEAK-271): fixed company numbers for the sales-specific
 * ROI screen, so a buyer sees a clear figure without entering their own
 * numbers. Participation and attribution are set to 100% on the assumption
 * that this is a hypothetical fully-engaged cohort, solely attributable to
 * the programme — not a measured or audited figure like the live-client
 * default above.
 */
export const DEMO_COMPANY_INPUTS: RoiCompanyInputs = {
  employees: 250,
  averageSalary: 45000,
  programmeCostPerEmployee: 400,
  costPerSickDay: 120,
  absenceDaysPerEmployee: 9.4,
  participationRate: 1,
  attributionRate: 1,
};

export type EvidenceStrength = 'strong' | 'good_indirect' | 'weakest';

export const EVIDENCE_STRENGTH_LABEL: Record<EvidenceStrength, string> = {
  strong: 'Strong evidence',
  good_indirect: 'Good evidence, indirect',
  weakest: 'Weakest evidence in this tool',
};

export interface LeverEvidence {
  studyFinding: string;
  bridgingAssumption: string;
  formula: string;
  overlapNote?: string;
}

export type RoiLeverId = 'sleepDuration' | 'sleepQuality' | 'restingHeartRate' | 'hrv' | 'vigorousActivity';

export interface RoiLeverDefinition {
  id: RoiLeverId;
  title: string;
  subtitle: string;
  unit: string;
  higherIsBetter: boolean;
  defaultThen: number;
  defaultNow: number;
  /** [low, high] change, in `unit`, that published research supports. */
  evidenceRange: [number, number];
  evidenceStrength: EvidenceStrength;
  evidence: LeverEvidence;
  /** £ value of moving from `then` to `now`, given the company's numbers. */
  convert: (then: number, now: number, company: RoiCompanyInputs) => number;
}

// Rosekind et al. (2010, JOEM, n=4,188) reported poor-sleep-quality workers
// lost $1,000-$1,900 more in yearly productivity than good sleepers. Midpoint
// $1,450, converted at ~0.74 USD→GBP (Aug 2026 spot rate) — recheck this rate
// periodically rather than letting it drift silently.
const SLEEP_QUALITY_GBP_GAP = 1074;

export const LEVERS: RoiLeverDefinition[] = [
  {
    id: 'sleepDuration',
    title: 'Sleep duration',
    subtitle: 'Average hours slept per night, cohort average.',
    unit: 'hrs',
    higherIsBetter: true,
    defaultThen: 6,
    defaultNow: 6.8,
    evidenceRange: [0.5, 1.5],
    evidenceStrength: 'strong',
    evidence: {
      studyFinding:
        'RAND Europe (2016) found under-6hr sleepers are 2.4% less productive than 7-9hr sleepers; reaching 6-7hrs recovers about 60% of that gap, so the recoverable ceiling is 2.4% × 60% = 1.44%.',
      bridgingAssumption:
        'RAND describes a category shift (under-6hrs vs 7-9hrs), not a per-hour rate. We scale linearly at 0.72 percentage points of productivity per hour improved, capped at the 1.44% ceiling (reached at 2 hours). That linear scaling is our assumption, not something RAND measured.',
      formula:
        'Productivity gain = the smaller of (hours improved × 0.72%) or 1.44%. £ = employees × participation rate × attribution rate × average salary × productivity gain.',
      overlapNote:
        'Shares underlying cause with sleep quality below: the combined total is an estimate, not two fully independent effects.',
    },
    convert: (then, now, c) => {
      const hoursImproved = Math.max(now - then, 0);
      const productivityGain = Math.min(hoursImproved * 0.0072, 0.0144);
      return c.employees * c.participationRate * c.attributionRate * c.averageSalary * productivityGain;
    },
  },
  {
    id: 'sleepQuality',
    title: 'Sleep quality',
    subtitle: 'Average sleep quality/efficiency score (%), cohort average.',
    unit: 'pts',
    higherIsBetter: true,
    defaultThen: 55,
    defaultNow: 68,
    evidenceRange: [10, 20],
    evidenceStrength: 'strong',
    evidence: {
      studyFinding: `Rosekind et al. (2010, JOEM, n=4,188) found poor-sleep-quality workers lost $1,000-$1,900 more in yearly productivity than good sleepers, about £${SLEEP_QUALITY_GBP_GAP.toLocaleString(
        'en-GB',
      )} at today's midpoint and exchange rate.`,
      bridgingAssumption: `Rosekind's study compared two discrete groups (good sleepers vs poor sleepers), not a continuous 0-100 score. To use your continuous quality score, we assume each 1 percentage-point rise in average score closes 1% of that £${SLEEP_QUALITY_GBP_GAP.toLocaleString(
        'en-GB',
      )} gap per employee. That 1-point-equals-1% conversion is our assumption for bridging into a WHOOP-style score, not something Rosekind measured.`,
      formula: `£ per employee = (points improved ÷ 100) × £${SLEEP_QUALITY_GBP_GAP.toLocaleString(
        'en-GB',
      )}. Total = employees × participation rate × attribution rate × that per-employee value.`,
      overlapNote: 'Shares underlying cause with sleep duration above.',
    },
    convert: (then, now, c) => {
      const pointsImproved = Math.max(now - then, 0);
      const perEmployee = (pointsImproved / 100) * SLEEP_QUALITY_GBP_GAP;
      return c.employees * c.participationRate * c.attributionRate * perEmployee;
    },
  },
  {
    id: 'restingHeartRate',
    title: 'Resting heart rate',
    subtitle: 'Average resting heart rate (bpm), cohort average. Lower is better.',
    unit: 'bpm',
    higherIsBetter: false,
    defaultThen: 68,
    defaultNow: 65,
    evidenceRange: [2, 5],
    evidenceStrength: 'good_indirect',
    evidence: {
      studyFinding:
        'A Swedish cohort (159 office workers) found higher cardiorespiratory fitness associated with lower odds of both sickness-absence duration (OR 0.92) and frequency (OR 0.93).',
      bridgingAssumption:
        'The study measured fitness level, not resting heart rate directly. A falling resting heart rate is a standard fitness marker, but we are one step removed from what was actually measured. We cap the absence-cost reduction at 1% per bpm improved, up to 8%. That cap is our assumption.',
      formula:
        'Reduction = the smaller of (bpm improved × 1%) or 8%. £ = employees × participation rate × attribution rate × reduction × (cost per sick day × average absence days).',
      overlapNote: 'Shares the same absence-cost pool as HRV below.',
    },
    convert: (then, now, c) => {
      const bpmImproved = Math.max(then - now, 0);
      const reduction = Math.min(bpmImproved * 0.01, 0.08);
      return (
        c.employees *
        c.participationRate *
        c.attributionRate *
        reduction *
        (c.costPerSickDay * c.absenceDaysPerEmployee)
      );
    },
  },
  {
    id: 'hrv',
    title: 'Heart rate variability (HRV)',
    subtitle: 'Average HRV (ms), cohort average. Higher is generally better.',
    unit: 'ms',
    higherIsBetter: true,
    defaultThen: 45,
    defaultNow: 50,
    evidenceRange: [3, 8],
    evidenceStrength: 'weakest',
    evidence: {
      studyFinding:
        'Sjörs et al. (2016, Biological Psychology) compared 54 clinically diagnosed burnout patients to 55 healthy controls and found measurably lower HRV in the burnout group. UK HSE data shows average stress-related absence lasts 22.9 working days.',
      bridgingAssumption:
        'A case-control comparison of diagnosed burnout vs healthy controls, not a working-population trend study; this is the weakest evidentiary link in this model. We cap the absence-cost reduction at 1% per ms improved, up to 10%. That cap is our assumption.',
      formula:
        'Reduction = the smaller of (ms improved × 1%) or 10%. £ = employees × participation rate × attribution rate × reduction × (cost per sick day × average absence days).',
      overlapNote: 'Shares the same absence-cost pool as resting heart rate above.',
    },
    convert: (then, now, c) => {
      const msImproved = Math.max(now - then, 0);
      const reduction = Math.min(msImproved * 0.01, 0.1);
      return (
        c.employees *
        c.participationRate *
        c.attributionRate *
        reduction *
        (c.costPerSickDay * c.absenceDaysPerEmployee)
      );
    },
  },
  {
    id: 'vigorousActivity',
    title: 'Vigorous activity',
    subtitle:
      'Average minutes per week at an elevated heart-rate zone (roughly 77%+ of estimated max HR), cohort average.',
    unit: 'min/wk',
    higherIsBetter: true,
    defaultThen: 20,
    defaultNow: 55,
    evidenceRange: [20, 40],
    evidenceStrength: 'strong',
    evidence: {
      studyFinding:
        'CDC Workplace Health Research Network: employees getting 75+ minutes/week of vigorous activity miss 4.1 fewer sick days/year than inactive employees.',
      bridgingAssumption:
        "This applies the CDC's own 75-minute threshold directly, not a proxy like calories or steps. It's the most evidence-direct lever in this tool, provided your device data can classify heart-rate zones as vigorous (roughly 77%+ of estimated max HR).",
      formula:
        'Fraction of the benefit reached = minutes ÷ 75, capped at 1, evaluated at both then and now. £ = employees × participation rate × attribution rate × (fraction now − fraction then) × 4.1 days × cost per sick day.',
    },
    convert: (then, now, c) => {
      const fracThen = Math.min(Math.max(then, 0) / 75, 1);
      const fracNow = Math.min(Math.max(now, 0) / 75, 1);
      const benefitGained = Math.max(fracNow - fracThen, 0);
      return c.employees * c.participationRate * c.attributionRate * benefitGained * 4.1 * c.costPerSickDay;
    },
  },
];

export const RETENTION_NOTE =
  'No published research connects wearable data to whether someone actually leaves. Shown as £0 until real evidence exists.';

export const PARTICIPATION_VS_ATTRIBUTION_NOTE =
  'Participation rate is the share of employees actively engaging with the programme - a measured figure from the Management Hub, not a judgement call. Attribution rate is the share of any improvement you credit to the programme rather than other causes (for example, changes in lifestyle outside work, reduced occupational stress, changes to working patterns, illness and acute stress, environmental factors) - this one genuinely is a judgement call.';

export const WHOOP_ACCURACY_DISCLAIMER =
  'All figures use WHOOP-measured biometrics (sleep, resting heart rate, HRV, heart-rate zones). Wearable sensors are consistent for tracking change over time within one person, but are not laboratory-grade instruments, so treat these figures as directional, not exact.';

export const MEASUREMENT_PERIOD_NOTE =
  '"Then" and "Now" should each be a cohort average over a sustained period (at least 28 days is recommended), not a single day. A one-day swing in resting heart rate or HRV is noise; a change sustained over weeks is what the cited research is measuring.';

export interface RoiLeverInputValue {
  then: number;
  now: number;
}

export type RoiLeverInputs = Record<RoiLeverId, RoiLeverInputValue>;

export const DEFAULT_LEVER_INPUTS: RoiLeverInputs = LEVERS.reduce(
  (acc, lever) => ({
    ...acc,
    [lever.id]: { then: lever.defaultThen, now: lever.defaultNow },
  }),
  {} as RoiLeverInputs,
);

// Business metrics — direct "then vs now total cost" figures the client
// reads off their own HR/payroll records, rather than a research-derived
// per-unit rate. No evidence/formula/participation/attribution scaling:
// these are confirmed actuals, not estimates.
export type BusinessMetricId = 'staffAbsence' | 'staffInjuries' | 'overtime' | 'staffTurnover' | 'employeeWellbeing';

export interface BusinessMetricDefinition {
  id: BusinessMetricId;
  title: string;
  subtitle: string;
}

export const BUSINESS_METRICS: BusinessMetricDefinition[] = [
  {
    id: 'staffAbsence',
    title: 'Staff absence days',
    subtitle: 'Total cost of staff absence, then vs now.',
  },
  {
    id: 'staffInjuries',
    title: 'Staff injuries',
    subtitle: 'Total cost of workplace injuries, then vs now.',
  },
  {
    id: 'overtime',
    title: 'Overtime hours',
    subtitle: 'Total cost of overtime hours worked, then vs now.',
  },
  {
    id: 'staffTurnover',
    title: 'Staff turnover',
    subtitle: 'Total cost of staff turnover (recruitment, onboarding, lost productivity), then vs now.',
  },
  {
    id: 'employeeWellbeing',
    title: 'Employee health and wellbeing',
    subtitle: 'Total cost of employee health and wellbeing claims/initiatives, then vs now.',
  },
];

export interface BusinessMetricInputValue {
  then: number;
  now: number;
}

export type BusinessMetricInputs = Record<BusinessMetricId, BusinessMetricInputValue>;

export const DEFAULT_BUSINESS_METRIC_INPUTS: BusinessMetricInputs = BUSINESS_METRICS.reduce(
  (acc, metric) => ({ ...acc, [metric.id]: { then: 0, now: 0 } }),
  {} as BusinessMetricInputs,
);

export interface BusinessMetricResult {
  id: BusinessMetricId;
  /** then − now; positive means cost went down (a saving). Shown as-is even if negative — it's reported data, not an estimate. */
  saving: number;
}

/**
 * Sales/demo preset (PEAK-271): every lever shows a flat 10% change from its
 * own baseline, in whichever direction counts as "better" for that metric,
 * so the sales screen reads as one consistent story instead of the mixed,
 * research-informed defaults above (meant for live-client editing).
 *
 * Workplace-injury savings are NOT set directly here — they're derived from
 * the sleep-duration change via the same formula live clients see
 * (`computeRoi` below), which scales toward a 7% ceiling and reaches it only
 * at a 2-hour improvement. A 10%-of-baseline sleep-duration bump falls well
 * short of that, so this demo intentionally shows whatever the real formula
 * produces rather than forcing a round "10%" — special-casing the cap just
 * for this preset would mean the sales screen no longer matches the real,
 * audited calculation every live client sees.
 */
export const DEMO_LEVER_INPUTS: RoiLeverInputs = LEVERS.reduce((acc, lever) => {
  const then = lever.defaultThen;
  // Rounded to 2dp — `then * 1.1`/`* 0.9` otherwise lands on binary-float noise
  // like 6.6000000000000005, which would render as a long decimal in the UI.
  const now = Math.round((lever.higherIsBetter ? then * 1.1 : then * 0.9) * 100) / 100;
  return { ...acc, [lever.id]: { then, now } };
}, {} as RoiLeverInputs);

export type RoiLeverVerdict = 'above_range' | 'in_range' | 'below_range';

export interface RoiLeverResult {
  id: RoiLeverId;
  /** now-minus-then for higher-is-better levers, then-minus-now otherwise — positive means improvement. */
  changeAchieved: number;
  value: number;
  topOfRangeValue: number;
  verdict: RoiLeverVerdict;
}

export interface RoiResult {
  levers: RoiLeverResult[];
  businessMetrics: BusinessMetricResult[];
  retentionSaving: 0;
  /** Sum of biometric lever values — modelled from research, not reported directly. */
  estimatedSavingsTotal: number;
  /** Sum of business-metric savings — reported company figures, then vs now. */
  confirmedSavingsTotal: number;
  /** confirmedSavingsTotal + estimatedSavingsTotal. */
  totalSavingsTotal: number;
  topOfRangeTotal: number;
  programmeCostTotal: number;
  netValue: number;
  returnPerPoundSpent: number;
  /** How much more is available if every lever reaches the top of its evidence-supported range. */
  upside: number;
}

/** Pure calculator. Given company numbers, lever then/now values, and business-metric then/now values, returns the full ROI breakdown. */
export const computeRoi = (
  company: RoiCompanyInputs,
  leverInputs: RoiLeverInputs,
  businessMetricInputs: BusinessMetricInputs,
): RoiResult => {
  let estimatedSavingsTotal = 0;
  let topOfRangeTotal = 0;

  const levers: RoiLeverResult[] = LEVERS.map((lever) => {
    const { then, now } = leverInputs[lever.id];
    const achieved = lever.higherIsBetter ? now - then : then - now;

    const value = lever.convert(then, now, company);
    const topNow = lever.higherIsBetter ? then + lever.evidenceRange[1] : then - lever.evidenceRange[1];
    const topOfRangeValue = lever.convert(then, topNow, company);

    const verdict: RoiLeverVerdict =
      achieved >= lever.evidenceRange[1]
        ? 'above_range'
        : achieved >= lever.evidenceRange[0]
          ? 'in_range'
          : 'below_range';

    estimatedSavingsTotal += value;
    topOfRangeTotal += topOfRangeValue;

    return { id: lever.id, changeAchieved: achieved, value, topOfRangeValue, verdict };
  });

  const businessMetrics: BusinessMetricResult[] = BUSINESS_METRICS.map((metric) => {
    const { then, now } = businessMetricInputs[metric.id];
    return { id: metric.id, saving: then - now };
  });
  const confirmedSavingsTotal = businessMetrics.reduce((sum, m) => sum + m.saving, 0);
  const totalSavingsTotal = confirmedSavingsTotal + estimatedSavingsTotal;

  const programmeCostTotal = company.employees * company.programmeCostPerEmployee;
  const netValue = totalSavingsTotal - programmeCostTotal;
  const returnPerPoundSpent = programmeCostTotal > 0 ? totalSavingsTotal / programmeCostTotal : 0;
  const upside = Math.max(topOfRangeTotal - estimatedSavingsTotal, 0);

  return {
    levers,
    businessMetrics,
    retentionSaving: 0,
    estimatedSavingsTotal,
    confirmedSavingsTotal,
    totalSavingsTotal,
    topOfRangeTotal,
    programmeCostTotal,
    netValue,
    returnPerPoundSpent,
    upside,
  };
};

export const formatMoney = (n: number): string => `£${Math.round(n).toLocaleString('en-GB')}`;
export const formatPct = (n: number): string => `${Math.round(n * 100)}%`;
